import { useState, useEffect, useCallback } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { Bell, LogOut, ChevronDown, UserRound, LayoutDashboard, CalendarCheck, BookOpenText, Plus, Fuel, FileOutput } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";
import { fmtDateTime } from "../lib/format";

const LOGO = "https://customer-assets-jt897jd0.emergentagent.net/job_drive-smart-25/artifacts/vqfz3t07_ENFLEET%20by%20POMEN.png";

const ALL = ["admin", "operator", "driver"];
const OPS = ["admin", "operator"];
const ALLP = ["admin", "operator", "driver", "pemandu"];

const MENUS = [
  { label: "Dashboard", to: "/", roles: ALLP, match: ["/"] },
  {
    label: "Buku Log", roles: ALLP, match: ["/tempahan", "/log-perjalanan", "/isi-minyak"],
    columns: [
      {
        heading: "Tempahan", roles: ALLP,
        items: [
          { label: "Semua Tempahan", to: "/tempahan", roles: ALLP },
          { label: "Kalendar Tempahan", to: "/tempahan?view=kalendar", roles: ALL },
          { label: "Barisan Menunggu", to: "/tempahan?status=menunggu", roles: OPS },
          { label: "Tempahan Diluluskan", to: "/tempahan?status=diluluskan", roles: ALL },
        ],
      },
      {
        heading: "Log Perjalanan", roles: ALL,
        items: [
          { label: "Semua Log", to: "/log-perjalanan", roles: ALL },
          { label: "Isi Minyak", to: "/isi-minyak", roles: ["admin", "operator", "pemandu"] },
        ],
      },
    ],
  },
  {
    label: "Kenderaan", roles: OPS, match: ["/kenderaan", "/pemandu/ketersediaan", "/modul/peta-langsung"],
    columns: [
      {
        heading: "Kenderaan", roles: OPS,
        items: [
          { label: "Senarai Kenderaan", to: "/kenderaan", roles: OPS },
          { label: "Ketersediaan Kenderaan", to: "/kenderaan/ketersediaan", roles: OPS },
          { label: "Ketersediaan Pemandu", to: "/pemandu/ketersediaan", roles: OPS },
          { label: "Daftar Kenderaan Baharu", to: "/kenderaan?new=1", roles: ["admin"] },
        ],
      },
      {
        heading: "Hayat Aset", roles: OPS,
        items: [
          { label: "Penyelenggaraan", to: "/kenderaan/penyelenggaraan", roles: OPS },
          { label: "Pelupusan", to: "/kenderaan/pelupusan", roles: OPS },
        ],
      },
      {
        heading: "GPS", roles: OPS,
        items: [
          { label: "Peta Langsung", to: "/modul/peta-langsung", roles: OPS },
        ],
      },
    ],
  },
  {
    label: "Tetapan", roles: ["admin"], match: ["/tetapan"],
    columns: [
      {
        heading: "Sistem", roles: ["admin"],
        items: [
          { label: "Profil Agensi", to: "/tetapan", roles: ["admin"] },
          { label: "Cawangan & Lokasi", to: "/tetapan/lokasi", roles: ["admin"] },
          { label: "Polisi & Kawalan Perjalanan", to: "/tetapan/polisi", roles: ["admin"] },
          { label: "Harga Minyak", to: "/tetapan/harga-minyak", roles: ["admin"] },
          { label: "No. Siri Laporan", to: "/tetapan/no-siri", roles: ["admin"] },
          { label: "Kategori Kenderaan", to: "/tetapan/kategori-kenderaan", roles: ["admin"] },
          { label: "Telematik", to: "/tetapan/telematik", roles: ["admin"] },
          { label: "Notifikasi & Emel", to: "/tetapan/notifikasi", roles: ["admin"] },
          { label: "Pengurusan Pengguna", to: "/tetapan/pengguna", roles: ["admin"] },
        ],
      },
    ],
  },
];

const ROLE_LABEL = { admin: "Pentadbir", operator: "Pengendali", driver: "Staf", pemandu: "Pemandu" };

function initials(name = "") {
  return name.split(" ").map((w) => w.replace(/[^A-Za-z]/g, "")).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

function slugify(label) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

function MegaMenu({ menu, role, isActive }) {
  const [open, setOpen] = useState(false);
  const columns = (menu.columns || [])
    .filter((c) => c.roles.includes(role))
    .map((c) => ({ ...c, items: c.items.filter((i) => i.roles.includes(role)) }))
    .filter((c) => c.items.length > 0);

  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        data-testid={`nav-menu-${slugify(menu.label)}`}
        className={`sidebar-link flex items-center gap-1.5 px-4 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px ${
          isActive || open
            ? "text-[#10056E] border-[#10056E] font-semibold"
            : "text-slate-500 border-transparent hover:text-[#10056E]"
        }`}
      >
        {menu.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-px z-50" data-testid={`mega-panel-${slugify(menu.label)}`}>
          <div className="bg-white border border-slate-200 rounded-b-xl shadow-xl p-7 flex gap-12 min-w-max fade-up">
            {columns.map((col) => (
              <div key={col.heading} className="min-w-44">
                <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400 font-semibold mb-3">{col.heading}</p>
                <ul className="space-y-0.5">
                  {col.items.map((it) =>
                    it.disabled ? (
                      <li key={it.label} className="px-2.5 py-2 text-sm text-slate-300 cursor-not-allowed select-none">{it.label}</li>
                    ) : (
                      <li key={it.label}>
                        <Link
                          to={it.to}
                          onClick={() => setOpen(false)}
                          data-testid={`nav-item-${slugify(it.label)}`}
                          className="block px-2.5 py-2 text-sm text-slate-700 rounded-md hover:text-[#10056E] hover:bg-indigo-50 transition-colors"
                        >
                          {it.label}
                        </Link>
                      </li>
                    )
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MobileTab({ to, icon: Icon, label }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      data-testid={`mobile-tab-${label.toLowerCase()}`}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors ${isActive ? "text-[#10056E]" : "text-slate-400"}`
      }
    >
      <Icon className="h-5 w-5" />
      {label}
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notif, setNotif] = useState({ items: [], unread: 0 });

  const loadNotif = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotif(data);
    } catch {}
  }, []);

  useEffect(() => {
    loadNotif();
    const t = setInterval(loadNotif, 30000);
    return () => clearInterval(t);
  }, [loadNotif]);

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    loadNotif();
  };

  const menus = MENUS.filter((m) => m.roles.includes(user?.role));

  const menuIsActive = (m) => {
    if (m.to === "/") return location.pathname === "/";
    return (m.match || []).some((p) => (p === "/" ? location.pathname === "/" : location.pathname.startsWith(p)));
  };

  return (
    <div className="min-h-screen bg-[#F6F7FB]">
      {/* Top dark bar */}
      <header className="bg-[#10056E] text-white" data-testid="topbar">
        <div className="max-w-[1440px] mx-auto px-6 h-16 flex items-center justify-between gap-6">
          <NavLink to="/" className="shrink-0" data-testid="topbar-logo">
            <img src={LOGO} alt="ENFLEET by POMEN" className="h-9 w-auto" />
          </NavLink>
          <div className="flex-1" />
          <div className="flex items-center gap-3 shrink-0">
            <span className="hidden lg:block text-xs text-white/70 max-w-52 truncate" data-testid="header-agency-name">{tenant?.name}</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="relative p-2 rounded-full hover:bg-white/10 transition-colors" data-testid="notification-bell">
                  <Bell className="h-5 w-5" />
                  {notif.unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full bg-[#DC3545] text-white text-[10px] flex items-center justify-center font-bold" data-testid="notification-unread-count">
                      {notif.unread}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-96 bg-white p-0" data-testid="notification-dropdown">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <p className="font-semibold text-sm">Notifikasi</p>
                  {notif.unread > 0 && (
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={markAllRead} data-testid="mark-all-read-btn">
                      Tanda semua dibaca
                    </Button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notif.items.length === 0 && <p className="p-4 text-sm text-muted-foreground">Tiada notifikasi.</p>}
                  {notif.items.map((n) => (
                    <div key={n.id} className={`px-4 py-3 border-b last:border-0 ${n.read ? "" : "bg-indigo-50/60"}`}>
                      <p className="text-sm font-semibold">{n.title}</p>
                      <p className="text-xs text-slate-600 mt-0.5">{n.message}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{fmtDateTime(n.created_at)}</p>
                    </div>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2.5 pl-1 pr-2 py-1 rounded-full hover:bg-white/10 transition-colors" data-testid="user-menu-trigger">
                  <span className="h-8 w-8 rounded-full bg-white text-[#10056E] text-xs font-bold flex items-center justify-center">
                    {initials(user?.name)}
                  </span>
                  <span className="hidden sm:block text-sm font-semibold" data-testid="header-user-name">{user?.name}</span>
                  <ChevronDown className="h-4 w-4 text-white/60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60 bg-white">
                <div className="px-3 py-2.5">
                  <p className="text-sm font-semibold">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  <Badge variant="secondary" className="mt-1.5 text-[10px]">{ROLE_LABEL[user?.role]}</Badge>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="profile-menu-item"
                  className="cursor-pointer"
                  onClick={() => navigate("/profil")}
                >
                  <UserRound className="h-4 w-4 mr-2" /> Profil Saya
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="logout-btn"
                  className="text-[#DC3545] cursor-pointer"
                  onClick={() => {
                    logout();
                    navigate("/login");
                  }}
                >
                  <LogOut className="h-4 w-4 mr-2" /> Log Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* White nav row with mega menus */}
      <nav className="hidden md:block bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm" data-testid="main-nav">
        <div className="max-w-[1440px] mx-auto px-6 flex items-center gap-1">
          {menus.map((m) =>
            m.columns ? (
              <MegaMenu key={m.label} menu={m} role={user?.role} isActive={menuIsActive(m)} />
            ) : (
              <NavLink
                key={m.label}
                to={m.to}
                data-testid={`nav-menu-${slugify(m.label)}`}
                className={`sidebar-link flex items-center gap-2 px-4 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px ${
                  menuIsActive(m)
                    ? "text-[#10056E] border-[#10056E] font-semibold"
                    : "text-slate-500 border-transparent hover:text-[#10056E]"
                }`}
              >
                {m.label}
              </NavLink>
            )
          )}
          <div className="flex-1" />
          {user?.role !== "pemandu" && (
            <button
              onClick={() => navigate("/tempahan/baharu")}
              data-testid="header-tempah-btn"
              className="my-1.5 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-white text-[#10056E] border border-[#10056E] text-sm font-semibold whitespace-nowrap shadow-sm hover:bg-indigo-50 hover:shadow-md transition-all"
            >
              <Plus className="h-4 w-4" /> Tempah
            </button>
          )}
          {["admin", "operator", "pemandu"].includes(user?.role) && (
            <button
              onClick={() => navigate("/isi-minyak/baharu")}
              data-testid="nav-isi-minyak-btn"
              className="my-1.5 ml-2 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-white text-[#10056E] border border-[#10056E] text-sm font-semibold whitespace-nowrap shadow-sm hover:bg-indigo-50 hover:shadow-md transition-all"
            >
              <Plus className="h-4 w-4" /> Isi Minyak
            </button>
          )}
          {OPS.includes(user?.role) && (
            <NavLink
              to="/jana-laporan"
              data-testid="nav-menu-jana-laporan"
              className={({ isActive }) =>
                `my-1.5 ml-2 inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-semibold whitespace-nowrap transition-all shadow-sm ${
                  isActive
                    ? "bg-[#10056E] text-white ring-2 ring-[#10056E]/25 shadow-md"
                    : "bg-[#10056E] text-white hover:bg-[#2a17a8] hover:shadow-md"
                }`
              }
            >
              <FileOutput className="h-4 w-4" /> Jana Laporan
            </NavLink>
          )}
        </div>
      </nav>

      <main className="max-w-[1440px] mx-auto px-4 md:px-6 py-6 md:py-8 pb-28 md:pb-8">{children}</main>

      {/* Mobile bottom navigation (PWA) */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 z-50 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]" data-testid="mobile-bottom-nav">
        <div className="grid grid-cols-5 h-16 items-stretch">
          <MobileTab to="/" icon={LayoutDashboard} label="Utama" />
          <MobileTab to="/tempahan" icon={CalendarCheck} label="Tempahan" />
          {user?.role !== "pemandu" ? (
            <>
              <div className="relative flex justify-center">
                <button
                  onClick={() => navigate("/tempahan/baharu")}
                  data-testid="mobile-tempah-fab"
                  className="absolute -top-6 h-14 w-14 rounded-full bg-[#10056E] text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform"
                >
                  <Plus className="h-7 w-7" />
                </button>
                <span className="self-end pb-1.5 text-[10px] font-semibold text-[#10056E]">Tempah</span>
              </div>
              <MobileTab to="/log-perjalanan" icon={BookOpenText} label="Log" />
              {user?.role === "driver"
                ? <MobileTab to="/profil" icon={UserRound} label="Profil" />
                : <MobileTab to="/isi-minyak" icon={Fuel} label="Isi Minyak" />}
            </>
          ) : (
            <>
              <div className="relative flex justify-center">
                <button
                  onClick={() => navigate("/isi-minyak/baharu")}
                  data-testid="mobile-refuel-fab"
                  className="absolute -top-6 h-14 w-14 rounded-full bg-[#10056E] text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform"
                >
                  <Fuel className="h-6 w-6" />
                </button>
                <span className="self-end pb-1.5 text-[10px] font-semibold text-[#10056E]">Isi Minyak</span>
              </div>
              <MobileTab to="/isi-minyak" icon={BookOpenText} label="Rekod" />
              <MobileTab to="/profil" icon={UserRound} label="Profil" />
            </>
          )}
        </div>
      </nav>
    </div>
  );
}
